// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Aggregates, Meta, NameRec, SeriesKey } from './types';

export interface Dataset {
  meta: Meta;
  names: NameRec[];
  agg: Aggregates;
  byKey: Map<string, NameRec>;
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Could not load ${url} (HTTP ${res.status})`);
  return (await res.json()) as T;
}

export async function loadDataset(signal?: AbortSignal): Promise<Dataset> {
  const [meta, names, agg] = await Promise.all([
    fetchJson<Meta>('data/meta.json', signal),
    fetchJson<NameRec[]>('data/names.json', signal),
    fetchJson<Aggregates>('data/aggregates.json', signal),
  ]);
  const byKey = new Map(names.map((n) => [n.k, n]));
  return { meta, names, agg, byKey };
}

/** Total babies a name has (across all series) — used for search ordering. */
export function nameWeight(n: NameRec): number {
  return n.totalCount;
}

/** Search names by prefix / substring, ranked by prefix match then popularity. */
export function searchNames(all: NameRec[], query: string, limit = 8): NameRec[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const scored: Array<{ n: NameRec; score: number }> = [];
  for (const n of all) {
    const lc = n.n.toLowerCase();
    let score = -1;
    if (lc === q) score = 100;
    else if (lc.startsWith(q)) score = 80;
    else if (lc.includes(q)) score = 40;
    if (score > 0) scored.push({ n, score: score + Math.min(n.totalCount, 100000) / 1e6 });
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.n);
}

/** Return the point for a given year in a series, or null. */
export function pointAt(n: NameRec, sk: SeriesKey, year: number): [number, number, number] | null {
  const arr = n.series[sk];
  if (!arr) return null;
  for (const p of arr) if (p[0] === year) return p;
  return null;
}

export const SEX_ORDER = ['boy', 'girl'] as const;
export const STATE_ORDER = ['NSW', 'QLD'] as const;
export const ALL_SERIES: SeriesKey[] = ['NSW-boy', 'NSW-girl', 'QLD-boy', 'QLD-girl'];
